import React from "react";
import { LogOut, User, Mail, Phone, Shield, Building2, Calendar, CheckCircle2, AlertCircle } from "lucide-react";
import userPic from "../assets/user.png";
import useAuth from "../useAuth";

export const ProfilePage = () => {
  const { user, logout } = useAuth();

  const InfoItem = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="h-10 w-10 bg-blue-50 rounded-full flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-blue-600" />
      </div>
      <div>
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">{label}</p>
        <p className="font-medium text-gray-900 mt-0.5">{value || "—"}</p>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
       {/* Page Header */}
       <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">My Profile</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your account settings and preferences.</p>
        </div>

      {/* Main Profile Card */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
                {/* Avatar Section */}
                <div className="relative shrink-0">
                    <div className="w-28 h-28 rounded-full bg-gray-50 border-4 border-white shadow-lg overflow-hidden">
                         <img
                            src={user?.profilePhoto || userPic}
                            alt="Profile"
                            className="w-full h-full object-cover"
                        />
                    </div>
                     <div className={`absolute bottom-2 right-2 h-5 w-5 rounded-full border-2 border-white ${user?.verified ? 'bg-green-500' : 'bg-amber-500'}`} title={user?.verified ? "Verified" : "Unverified"}></div>
                </div>

                {/* User Details */}
                <div className="flex-1 text-center md:text-left space-y-2">
                    <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                        {user?.fullName || "User"}
                    </h2>
                    
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                        <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold border border-blue-100">
                            <Shield className="h-3.5 w-3.5" />
                            {user?.designation || user?.userType || "User"}
                        </span>
                        
                        {user?.verified ? (
                            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-50 text-green-700 text-sm font-semibold border border-green-100">
                                <CheckCircle2 className="h-3.5 w-3.5" /> Verified Account
                            </span>
                        ) : (
                             <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-sm font-semibold border border-amber-100">
                                <AlertCircle className="h-3.5 w-3.5" /> Action Required
                            </span>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="mt-4 md:mt-0">
                    <button
                        onClick={logout}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 shadow-sm transition-all focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                        <LogOut className="h-4 w-4" />
                        Sign Out
                    </button>
                </div>
            </div>
        </div>

            <div className="border-t border-gray-100 pt-6">
                <h3 className="text-sm font-bold text-gray-900 mb-4 ml-3">Account Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem 
                        icon={Phone} 
                        label="Mobile Number" 
                        value={user?.mobileNo} 
                    />
                    <InfoItem 
                        icon={Shield} 
                        label="User Role" 
                        value={user?.userType} 
                    />
                     <InfoItem 
                        icon={Mail} 
                        label="Username / Email" 
                        value={user?.email || user?.username || "—"} 
                    />
                    <InfoItem 
                        icon={Building2} 
                        label="Department" 
                        value={user?.receivedDepartment || "General"} 
                    />
                    <InfoItem 
                        icon={Calendar} 
                        label="Joined On" 
                        value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : null} 
                    />
                     <InfoItem 
                        icon={User} 
                        label="User ID" 
                        value={`#${user?.id || "000"}`} 
                    />
                </div>
            </div>
        </div>
      </div>
  );
};
